import Keycloak from 'keycloak-js';

const _kc = new Keycloak('/keycloak.json');

const initKeycloak = (onAuthenticatedCallback) => {
	_kc
		.init({
			onLoad: 'check-sso',
			silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
			pkceMethod: 'S256',
			enableLogging: true,
		})
		.then((authenticated) => {
			if (!authenticated) {
				console.log('user is not authenticated..!');
			}

			onAuthenticatedCallback();
		})
		.catch(console.error);
};

const doLogin = _kc.login;

const doLogout = _kc.logout;

const getToken = () => _kc.token;

const getRealmAccessRoles = () => {
	if (_kc.tokenParsed?.realm_access?.roles)
		return _kc.tokenParsed.realm_access.roles;
	else return [];
};

const getResourceAccessRoles = () => {
	const resourceAccess = _kc.tokenParsed?.resource_access;
	if (!resourceAccess) return [];

	const clientId =
		_kc.clientId ||
		_kc.tokenParsed?.azp ||
		_kc.tokenParsed?.issued_for;

	if (clientId && Array.isArray(resourceAccess?.[clientId]?.roles)) {
		return resourceAccess[clientId].roles;
	}

	return Object.values(resourceAccess)
		.flatMap((v) => (Array.isArray(v?.roles) ? v.roles : []))
		.filter(Boolean);
};

const isLoggedIn = () => !!_kc.token;

const updateToken = (successCallback) =>
	_kc.updateToken(5).then(successCallback).catch(doLogin);

const ensureFreshToken = (minValiditySeconds = 5) =>
	_kc.updateToken(minValiditySeconds);

const getUsername = () => _kc.tokenParsed?.preferred_username;
const getFirstName = () => _kc.tokenParsed?.given_name;
const getLastName = () => _kc.tokenParsed?.family_name;

const getGroups = () => {
	const groups = _kc.tokenParsed?.groups;
	return Array.isArray(groups) ? groups : [];
};

const hasRole = (role) => {
	if (!role) return false;
	const normalize = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
	const target = normalize(role);

	const realmRoles = getRealmAccessRoles().map((r) => normalize(r));
	if (realmRoles.includes(target)) return true;

	const resourceRoles = getResourceAccessRoles().map((r) => normalize(r));
	if (resourceRoles.includes(target)) return true;

	const groupToRoleAliases = {
		ngomadministrators: ["ngomadministrator"],
		ngombusinessusers: ["ngombusinessuser"],
		ngomdataviewers: ["ngomdataviewer"],
		ngompowerusers: ["ngompoweruser"],
	};

	const groups = getGroups()
		.map((g) => String(g).split("/").filter(Boolean).pop())
		.filter(Boolean)
		.map((g) => normalize(g));

	for (const g of groups) {
		if (g === target) return true;
		const singular = g.endsWith("s") ? g.slice(0, -1) : g;
		if (singular === target) return true;
		const aliases = groupToRoleAliases[g] || groupToRoleAliases[singular];
		if (aliases?.includes(target)) return true;
	}

	return _kc.hasRealmRole(role) || _kc.hasResourceRole(role);
};

const getKC = () => _kc;

const UserService = {
	initKeycloak,
	doLogin,
	doLogout,
	isLoggedIn,
	getToken,
	updateToken,
	ensureFreshToken,
	getUsername,
	getFirstName,
	getLastName,
	hasRole,
	getGroups,
	getRealmAccessRoles,
	getResourceAccessRoles,
	getKC,
};

export default UserService;
